import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DimpleDivider from '@tds/core-dimple-divider';
import Heading from '@tds/core-heading';
import Text from '@tds/core-text';
import TextArea from '@tds/core-text-area';
import axios from 'axios';

import { apiHost, config } from '../../../utils/request';
import { Box } from '../../../design/css';
import { popupStyles } from '../css';
import OrderButton from './orderButton';
import Modal from '../../../design/shared/modal';
import { useLocaleContext } from '../../../context/LocaleContext';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { orderStatus as orderStatusConsts } from '../../../utils/constant';
import EmailInput from './emailInput';

const defaultDisabledStatus = {
  name: '',
  value: 'SELECT_AN_OPTION'
};

const defaultDisabledReasonForReturning = {
  name: '',
  value: 'SELECT_AN_OPTION',
  flag: ''
};

const OrderAction = ({
  orderStatus,
  orderId,
  onSuccess,
  onSave,
  comment,
  addingComment
}) => {
  const locale = useLocaleContext();
  const [selectedStatus, setSeletedStatus] = useState(defaultDisabledStatus);
  const [selectedReason, setSeletedReason] = useState(
    defaultDisabledReasonForReturning
  );
  const [customReason, setCustomReason] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [emails, setEmails] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [emailError, setEmailError] = useState('');

  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        actions: {
          orderQuestion,
          statuses,
          reasonForReturningOrder,
          returnSalesReasons,
          confirm
        }
      }
    },
    ORDERS: { save },
    DEFECT_MANAGEMENT: { ADD_COMMENT },
    USERS: {
      USER_AUTHENTICATION: {
        labels: { SELECT_OPTION, PLACEHOLDER_EMAIL }
      }
    }
  } = locale === 'en-US' ? EN : FR;

  const isOtherReasonOptionSelected =
    selectedReason.value === returnSalesReasons[2].value;

  const isEmailValid = (!!emails.length && !!body) || (!emails.length && !body);

  const isSubmitEnabled = () => {
    if (selectedStatus.value === orderStatusConsts.waitingOnSales) {
      if (isOtherReasonOptionSelected) {
        if (customReason) {
          return true;
        }
      } else if (
        selectedReason.value &&
        selectedReason.value !== defaultDisabledReasonForReturning.value
      ) {
        return true;
      }
    } else if (selectedStatus.value === orderStatusConsts.canceled) {
      return true;
    } else if (selectedStatus.value === orderStatusConsts.submitted) {
      return true;
    }
    return false;
  };

  const resetPopupValues = () => {
    setSeletedReason(defaultDisabledReasonForReturning);
    setCustomReason('');
    setEmails([]);
    setSubject('');
    setBody('');
    setEmailError('');
  };

  const closePopup = () => {
    setPopupOpen(false);
    setSeletedStatus(defaultDisabledStatus);
    resetPopupValues();
  };

  const onPopupClose = () => {
    closePopup();
  };

  const onStatusChange = e => {
    if (e.target.value === orderStatusConsts.waitingOnSales) {
      setPopupOpen(true);
    }
    setSeletedStatus({
      name: e.target.options[e.target.selectedIndex].text,
      value: e.target.value
    });
  };

  const onReasonChange = e => {
    setSeletedReason({
      name: e.target.options[e.target.selectedIndex].text,
      flag: e.target.options[e.target.selectedIndex].getAttribute('data-flag'),
      value: e.target.value
    });
  };

  const onCustomReasonChange = e => {
    setCustomReason(e.target.value);
  };

  const onEmailSubmit = () => {
    const payload = { to: emails, text: body };
    if (subject) payload.subject = subject;
    axios
      .post(`${apiHost}/send-email`, payload, config())
      .then(() => {
        onSuccess();
      })
      .catch(() => {});
  };

  const onSubmit = () => {
    onSave(); // Run parent functions submit function
    const payload = {
      status: selectedStatus.value
    };
    if (selectedStatus.value !== defaultDisabledStatus.value) {
      if (isEmailValid) {
        onEmailSubmit();
      } else {
        setEmailError('Email body or address is missing');
        return;
      }
      if (
        selectedReason.value &&
        selectedReason.value !== defaultDisabledReasonForReturning.value
      ) {
        payload.flag = selectedReason.flag;
        payload.isFlagAdded = true;
        payload.step = 1;
        payload.reasonForReturningOrder = selectedReason.value;
        if (isOtherReasonOptionSelected) {
          payload.customReason = customReason;
        }
      }
      setSubmitting(true);
      axios
        .put(`${apiHost}/orders/${orderId}`, payload, config())
        .then(() => {
          onSuccess();
        })
        .catch(() => {})
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  return (
    <>
      <DimpleDivider />
      <Heading level="h2" tag="h4">
        {orderQuestion}
      </Heading>
      <div
        className="frg-select-container color-light"
        style={{
          position: 'relative',
          width: '100%',
          height: '50px',
          marginTop: '1.5rem'
        }}
      >
        <select
          style={{ fontSize: '1rem' }}
          value={selectedStatus.value}
          onChange={onStatusChange}
        >
          <option selected disabled value="SELECT_AN_OPTION">
            {SELECT_OPTION}
          </option>
          {statuses[orderStatus].map(status => (
            <option value={status.value}>{status.name}</option>
          ))}
        </select>
      </div>
      <Box
        display="flex"
        justifyContent="space-evenly"
        flexDirection="row"
        width={100}
        mdWidth={100}
        pr={16}
        pl={16}
        pt={28}
        pb={24}
      >
        <OrderButton
          label={save}
          onClick={onSubmit}
          disabled={
            selectedStatus.value === defaultDisabledStatus.value && !comment
          }
          variant="purple"
          isLoading={isSubmitting}
        />
      </Box>
      <Modal
        open={popupOpen}
        setOpen={onPopupClose}
        customStyles={popupStyles}
        header={reasonForReturningOrder}
      >
        <Text>{reasonForReturningOrder}</Text>
        <Box mt={5} />
        <select
          style={{ fontSize: '1rem' }}
          value={selectedReason.value}
          onChange={onReasonChange}
        >
          <option
            selected
            disabled
            value={defaultDisabledReasonForReturning.value}
          >
            {SELECT_OPTION}
          </option>
          {returnSalesReasons.map(({ name, value, flag }) => (
            <option value={value} data-flag={flag}>
              {name}
            </option>
          ))}
        </select>
        <Box mt={10} />
        {isOtherReasonOptionSelected && (
          <TextArea
            label={ADD_COMMENT}
            value={customReason}
            maxLength={100}
            onChange={onCustomReasonChange}
          />
        )}
        <Box mt={10} />
        <Text>{PLACEHOLDER_EMAIL}</Text>
        <EmailInput
          emails={emails}
          setEmails={setEmails}
          body={body}
          setBody={setBody}
          subject={subject}
          setSubject={setSubject}
          hasError={!!emailError}
        />
        <span style={{ color: 'red', fontSize: 12 }}>{emailError}</span>
        <Box mt={6} />
        <Box
          display="flex"
          justifyContent="center"
          width="100"
          style={{ alignItems: 'center' }}
        >
          <Box>
            <OrderButton
              isLoading={isSubmitting || addingComment}
              label={confirm}
              disabled={!isSubmitEnabled()}
              onClick={onSubmit}
              variant="purple"
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

OrderAction.propTypes = {
  orderId: PropTypes.number.isRequired,
  orderStatus: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  comment: PropTypes.string.isRequired,
  addingComment: PropTypes.bool.isRequired
};

export default OrderAction;
